import { strFromU8, Unzip, UnzipInflate, zipSync } from "fflate";

import { RowLimitExceededError } from "./csv-parse-error";

const MAX_EXPANDED_BYTES = 8 * 1024 * 1024;
const ZIP_CHUNK_BYTES = 1024;
const MAX_COLUMNS = 256;
const MAX_ROWS = 10_000;
const MAX_ENTRIES = 1024;

export interface XlsxArchiveLimits {
  readonly maxExpandedBytes: number;
  readonly maxRows: number;
}

export async function readBoundedXlsxArchive({
  file,
  limit,
  limits = { maxExpandedBytes: MAX_EXPANDED_BYTES, maxRows: MAX_ROWS },
}: {
  file: File;
  limit: number;
  limits?: XlsxArchiveLimits;
}): Promise<ArrayBuffer> {
  const archive = new Uint8Array(await file.arrayBuffer());
  const contentByPath = expandBoundedArchive({ archive, maxExpandedBytes: limits.maxExpandedBytes });
  const worksheet = readFirstWorksheet(contentByPath);
  assertWorksheetLimits({ worksheet, limit, maxRows: limits.maxRows });
  return new Uint8Array(zipSync(Object.fromEntries(contentByPath), { level: 0 })).buffer;
}

function expandBoundedArchive({
  archive,
  maxExpandedBytes,
}: {
  archive: Uint8Array;
  maxExpandedBytes: number;
}): Map<string, Uint8Array> {
  const contentByPath = new Map<string, Uint8Array>();
  let expandedBytes = 0;
  let entryCount = 0;
  const unzip = new Unzip((entry) => {
    if (++entryCount > MAX_ENTRIES) throw new Error("invalid");
    const chunkList: Uint8Array[] = [];
    let entryBytes = 0;
    entry.ondata = (error, chunk, isFinal) => {
      if (error) throw error;
      expandedBytes += chunk.length;
      if (expandedBytes > maxExpandedBytes) throw new Error("invalid");
      entryBytes += chunk.length;
      chunkList.push(chunk);
      if (!isFinal) return;
      const content = new Uint8Array(entryBytes);
      let offset = 0;
      for (const part of chunkList) {
        content.set(part, offset);
        offset += part.length;
      }
      contentByPath.set(entry.name, content);
    };
    entry.start();
  });
  unzip.register(UnzipInflate);
  for (let offset = 0; offset < archive.length; offset += ZIP_CHUNK_BYTES) {
    unzip.push(archive.subarray(offset, offset + ZIP_CHUNK_BYTES), offset + ZIP_CHUNK_BYTES >= archive.length);
  }

  return contentByPath;
}

function readFirstWorksheet(contentByPath: Map<string, Uint8Array>): Document {
  const workbook = parseXml(contentByPath.get("xl/workbook.xml"));
  const firstSheet = workbook.getElementsByTagNameNS("*", "sheet")[0];
  const relationId =
    firstSheet?.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id") ??
    firstSheet?.getAttributeNS("http://purl.oclc.org/ooxml/officeDocument/relationships", "id");
  const relations = parseXml(contentByPath.get("xl/_rels/workbook.xml.rels"));
  const relation = Array.from(relations.getElementsByTagNameNS("*", "Relationship")).find(
    (element) => element.getAttribute("Id") === relationId,
  );
  const target = relation?.getAttribute("Target");
  if (!target) throw new Error("invalid");
  const path = target.startsWith("/") ? target.slice(1) : `xl/${target}`;
  return parseXml(contentByPath.get(path));
}

function assertWorksheetLimits({
  worksheet,
  limit,
  maxRows,
}: {
  worksheet: Document;
  limit: number;
  maxRows: number;
}): void {
  const rowLimit = Math.min(limit, maxRows) + 1;
  const rowList = worksheet.getElementsByTagNameNS("*", "row");
  if (rowList.length > rowLimit) throw new RowLimitExceededError();
  for (const row of Array.from(rowList)) {
    const rowNumber = Number(row.getAttribute("r"));
    if (row.hasAttribute("r") && (!Number.isSafeInteger(rowNumber) || rowNumber < 1)) {
      throw new Error("invalid");
    }
    if (rowNumber > rowLimit) throw new RowLimitExceededError();
    const cellList = row.getElementsByTagNameNS("*", "c");
    if (cellList.length > MAX_COLUMNS) throw new Error("invalid");
    for (const cell of Array.from(cellList)) {
      const address = /^([A-Z]+)([1-9]\d*)$/.exec(cell.getAttribute("r") ?? "");
      if (!address) throw new Error("invalid");
      const column = Array.from(address[1]).reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0);
      const cellRowNumber = Number(address[2]);
      if (column > MAX_COLUMNS || !Number.isSafeInteger(cellRowNumber)) throw new Error("invalid");
      if (cellRowNumber > rowLimit) throw new RowLimitExceededError();
    }
  }
}

function parseXml(content: Uint8Array | undefined): Document {
  if (!content) throw new Error("invalid");
  const document = new DOMParser().parseFromString(strFromU8(content), "application/xml");
  if (document.getElementsByTagName("parsererror").length) throw new Error("invalid");
  return document;
}
